/* eslint-disable */
import { useContext, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Box, Typography, FormGroup, FormControlLabel, Button, Stack, Divider, IconButton, InputAdornment, styled, Autocomplete, useTheme } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import CustomCheckbox from "../forms/theme-elements/CustomCheckbox";
import CustomTextField from "../forms/theme-elements/CustomTextField";
import CustomFormLabel from "../forms/theme-elements/CustomFormLabel";
import { usePopup } from "../../context/commonPop";
import { checkEmail, login, verifyLink } from "../../gql/mutations/auth";
import { validateEmail, validatePassword, validateString } from "../Validate/validate";
import { loginUser, setThemeConfig } from "../../redux/action";
import useQuery from "../../hooks/allQuery";
import LoginGoogle from "../../googleAuth/loginGoogle";
import { saveLocalTokens, saveSessionTokens } from "../../utils/session";
import VerifyPopup from "../../common/verifyPopup";
import { popupType } from "../../common/constant";
import { debounce } from "lodash";
import { isValidEmail } from "../../utils/validations";
import { AuthContext } from "../../AuthContext";
import { encryptPassword } from "../../utils/myUtills";

const PassWordShowIconWrapper = styled(InputAdornment)(({ theme }) => ({
  "& button": {
    "&:focus": {
      outline: "none",
    },
    "& svg": {
      color: "#0000008a",
    },
    "& input:is(:autofill)": {
      boxShadow: "0 0 0 100px #e8f0fe inset",
      WebkitTextFillColor: "#000000",
      caretColor: "black",
      "& + fieldset": { borderColor: "#DFE5EF" },
    },
  },
}));

function AuthLogin({ title, subtitle, subtext }) {
  const theme = useTheme();
  const [FIRSTCALL] = useMutation(verifyLink);
  const [AUTH_USER] = useMutation(login);
  const { setIsCreateProject } = useContext(AuthContext);
  const [checkEmailQuery] = useLazyQuery(checkEmail, { fetchPolicy: "network-only" });
  const navigate = useNavigate();
  const { allQuery } = useQuery();
  const dispatch = useDispatch();
  const { handleOpen, customPopupModel } = usePopup();
  const [verifyPopupOpen, setVerifyPopupOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
    isRemembered: true,
  });
  const [emailForPopup, setEmailForPopup] = useState("");
  const [errorState, setErrorState] = useState({
    email: false,
    password: false,
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const onValueChange = e => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));

    if (name === "email") {
      debouncedCheckEmail(value);
      setErrorState(prev => ({ ...prev, name: validateEmail(value) }));
    }
    if (name === "password") {
      setErrorState(prev => ({ ...prev, password: validatePassword(value) }));
    }
  };
  const isFormValid = !errorState.email && !errorState.password && loginData.email.trim() !== "" && loginData.password.trim() !== "";
  const checkError = async data => {
    const email = await validateEmail(data.email);
    const password = await validateString(data.password);

    await setErrorState({
      ...errorState,
      email,
      password,
    });
    if (email || password) {
      return true;
    }
    return false;
  };

  const debouncedCheckEmail = debounce(async newEmail => {
    if (isValidEmail(newEmail)) {
      try {
        const { data } = await checkEmailQuery({ variables: { email: newEmail } });

        if (!data?.checkEmail) {
          setErrorState(prev => ({ ...prev, email: "No user found for this email" }));
        } else {
          setErrorState(prev => ({ ...prev, email: false }));
        }
      } catch (err) {
        console.log("GraphQL Error: ", err);
      }
    }
  }, 800);

  const HandleSubmit = async () => {
    if (!emailForPopup) return;
    allQuery({
      QUERY: FIRSTCALL,
      variables: {
        email: emailForPopup,
      },
      onCompleteFunc: ({ sendVerifyLink }) => {
        handleOpen(true, sendVerifyLink?.message || "Verification Link sent successfully on your mail");
      },
      onErrorFunc: error => {
        console.log("error", error);
        handleOpen(true, error?.message || "Something went wrong");
      },
    });
    setVerifyPopupOpen(false);
  };

  const handleLoginUser = async e => {
    e.preventDefault();

    try {
      const check = await checkError(loginData);
      if (check) return;

      const encryptedPassword = encryptPassword(loginData.password);
      const updatedLoginData = { ...loginData, password: encryptedPassword };
      allQuery({
        QUERY: AUTH_USER,
        variables: {
          data: updatedLoginData,
        },
        onCompleteFunc: data => {
          const { accessToken, refreshToken, isCreateProject } = data.login;
          const { isRemembered } = data?.login?.user;

          if (isRemembered) {
            localStorage.setItem("token", accessToken);
            saveLocalTokens({ accessToken, refreshToken });
          } else {
            sessionStorage.setItem("token", accessToken);
            saveSessionTokens({ accessToken, refreshToken });
          }

          setIsCreateProject(isCreateProject);

          dispatch(setThemeConfig(data.login.user?.userTheme));
          dispatch(
            loginUser({
              ...data.login.user,
              subscription: data?.login?.subscription,
              subscriptionCredits: data?.login?.subscriptionCredits,
            }),
          );

          window.dispatchEvent(new Event("storage"));
          navigate(isCreateProject ? "/dashboard?menu=dashboard" : "/project-config");
        },
        onErrorFunc: errdata => {
          const { message, isVerificationLink } = JSON.parse(errdata?.message) || {};
          if (isVerificationLink) {
            setEmailForPopup(loginData.email);
            setVerifyPopupOpen(true);
          } else {
            customPopupModel({
              type: popupType.failed,
              buttonType: popupType.failed,
              contentFunc: <Typography component="p">{message || "Something went wrong"}</Typography>,
            });
          }
        },
      });
    } catch (error) {
      console.log(error);
      handleOpen(true, "Something went wrong");
    }
  };

  const handleNavigate = () => {
    navigate("/forgot", { state: { email: loginData.email } });
  };

  const handleFocus = e => {
    e.target.removeAttribute("readonly");
  };

  return (
    <>
      {verifyPopupOpen && <VerifyPopup open={verifyPopupOpen} setOpen={setVerifyPopupOpen} handleSubmit={HandleSubmit} email={loginData.email} />}
      {title ? (
        <Typography fontWeight="700" variant="h3" mb={1}>
          {title}
        </Typography>
      ) : null}

      {subtext}
      <Box>
        <LoginGoogle buttonLabel="continue_with" />
      </Box>
      <Box mt={3}>
        <Divider>
          <Typography component="span" color="textSecondary" variant="h6" fontWeight="400" position="relative" px={2}>
            or sign in with
          </Typography>
        </Divider>
      </Box>

      <Stack>
        <Box
          sx={{
            "& .MuiFormHelperText-root": {
              color: "red",
            },
          }}
        >
          <CustomFormLabel htmlFor="email">Email Address</CustomFormLabel>
          <CustomTextField
            id="email"
            type="email"
            variant="outlined"
            fullWidth
            autoComplete="off"
            inputProps={{
              autoComplete: "email",
              autoCapitalize: "none",
              inputMode: "email",
            }}
            name="email"
            value={loginData.email}
            onFocus={handleFocus}
            onChange={onValueChange}
            onKeyDown={e => e.key === "Enter" && handleLoginUser(e)}
            error={Boolean(errorState.email)}
            helperText={
              !isValidEmail(loginData.email) && loginData.email.length > 0
                ? "Please enter a valid email address."
                : errorState.email && loginData.email.length === 0
                ? "Email field cannot be empty."
                : errorState.email
            }
            sx={{
              "& input:is(:autofill)": {
                boxShadow: "0 0 0 100px #e8f0fe inset",
                WebkitTextFillColor: "#000000",
                caretColor: "#000000",
                "& + fieldset": { borderColor: "#DFE5EF !important" },
              },
            }}
          />
        </Box>
        <Box
          sx={{
            "& .MuiFormHelperText-root": {
              color: "red",
            },
          }}
        >
          <CustomFormLabel htmlFor="password">Password</CustomFormLabel>
          <CustomTextField
            id="password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            name="password"
            fullWidth
            autoComplete="off"
            inputProps={{
              autoComplete: "new-password",
              form: "off",
              autoCapitalize: "none",
              inputMode: "none",
            }}
            value={loginData.password}
            onChange={e => onValueChange(e)}
            error={errorState.password}
            onKeyDown={e => e.key === "Enter" && handleLoginUser(e)}
            helperText={errorState.password && (!loginData.password ? "Please enter a password" : "Invalid password format")}
            sx={{
              "& input:is(:autofill)": {
                boxShadow: "0 0 0 100px #e8f0fe inset",
                WebkitTextFillColor: "#000000",
                caretColor: "#000000",
                "& + .MuiInputAdornment-root + fieldset": { borderColor: "#DFE5EF" },
              },
            }}
            InputProps={{
              endAdornment: (
                <PassWordShowIconWrapper position="end">
                  <IconButton onClick={togglePasswordVisibility} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </PassWordShowIconWrapper>
              ),
            }}
          />
        </Box>
        <Stack justifyContent="space-between" direction="row" alignItems="center" my={2}>
          <FormGroup sx={{ "& label": { marginBottom: "0" } }}>
            {/* <FormControlLabel control={<CustomCheckbox defaultChecked sx={{ "&.Mui-checked": { "& > span": { backgroundColor: "#5D87FF" } } }} />} label="Remember this Device" /> */}
            <FormControlLabel
              control={
                <CustomCheckbox
                  sx={{ "&.Mui-checked": { "& > span": { backgroundColor: "#5D87FF" } } }}
                  checked={loginData.isRemembered}
                  onChange={() => {
                    setLoginData(prev => {
                      const a = { ...prev, isRemembered: !prev.isRemembered };
                      return a;
                    });
                  }}
                />
              }
              label="Remember this Device"
            />
          </FormGroup>
          <Typography
            onClick={handleNavigate}
            fontWeight="500"
            sx={{
              textDecoration: "none",
              color: "#5D87FF",
              cursor: "pointer",
            }}
          >
            Forgot Password ?
          </Typography>
        </Stack>
      </Stack>
      <Box>
        <Button
          sx={{
            backgroundColor: "#5D87FF",
            color: "#fff",
            transition: "0.3s all",
            "&:hover": {
              opacity: "0.9",
              backgroundColor: "#5D87FF",
            },
            "&.Mui-disabled": {
              color: "#ccc",
              backgroundColor: "#e0e0e0",
            },
          }}
          size="large"
          fullWidth
          component={Link}
          to="/dashboard?menu=dashboard"
          type="submit"
          onClick={e => handleLoginUser(e)}
          disabled={!isFormValid}
        >
          Sign In
        </Button>
      </Box>
      {subtitle}
    </>
  );
}

export default AuthLogin;
